import React, { ReactElement } from 'react';

import SearchableMenu, {
  TItemToString,
} from 'components/base/Menu/SearchableMenu';

import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import CustomerIcon from '../../CustomerIcon/CustomerIcon';
import { RecentFilter } from 'components/TopicFilters/TopicFilters.styles';
import colors from 'styles/themes/colors';
import { ThemeProps } from 'types/styled';
import styled, { css } from 'styled-components/macro';
import variables from 'styles/themes/variables';
import DropdownButton from '../DropdownButton';
import DropdownWrapper from '../DropdownWrapper';

const hoveredStyles = (isActive: any): any => css`
  background: ${colors.Sand};
  span {
    transform: ${!isActive && 'translateX(5px)'};
  }
`;

export const ListItemBox = styled(Box)<ThemeProps<'div'>>`
  ${({ isActive, isHovered }): any => css`
    cursor: pointer;
    background: ${isActive || isHovered ? colors.Sand : 'white'};

    span {
      display: inline-block;
      transition: ${`150ms ${variables.timingFunction} transform`};
    }

    &:hover,
    &[aria-selected='true'] {
      ${hoveredStyles(isActive)}
    }

    ${isHovered && hoveredStyles(isActive)}
  `}
`;

const ListItem: React.FC<IListItemProps> = (props) => {
  const { item, isActive, isHovered } = props;

  return (
    <ListItemBox
      isActive={isActive}
      isHovered={isHovered}
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      py={2}
      paddingRight={2}
      paddingLeft={3}
    >
      <Box
        alignSelf="auto"
        display="inline-block"
        minWidth="2rem"
        maxHeight="1rem"
      >
        {item.name && (
          <CustomerIcon
            small
            aria-hidden
            customer={item.name}
            isDownloadsDsp={item.feedType === 'downloads'}
          />
        )}
      </Box>
      <Span marginLeft={1} alignSelf="auto">
        {item.label}
      </Span>
      <RecentFilter>{item.isRecent ? 'Recent' : ''}</RecentFilter>
    </ListItemBox>
  );
};

const getButtonWithDefaultLabel = (
  clearFilter?: () => void,
  defaultLabel?: string
): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return (
      <DropdownButton
        {...props}
        defaultLabel={defaultLabel}
        dropdownType="DSP"
        clearFilter={clearFilter}
      />
    );
  };
  return LabeledButton;
};

const TopicFilterCustomerDropdown: React.FC<
  ITopicFilterCustomerDropdownProps
> = (props) => {
  const {
    items,
    selectedItem,
    onChange,
    disabled,
    disabledMessage,
    itemToString,
    defaultLabel,
    children,
    isSubscriptionForm = false,
    clearFilter,
    ListLabel,
    onClickOutsideCustom,
    customLabel,
    customItem,
  } = props;

  // selectedItem can be undefined which will trigger React into thinking this is a controlled component,
  // when it then switches to an actual value runtime errors will be thrown since React will figure
  // we're trying to switch a conrtolled component into an uncontrolled component. Null is a valid value,
  // therefore we add this seemingly weird check.

  const label =
    customLabel ||
    `DSP${
      !selectedItem
        ? ''
        : selectedItem.feedType === 'streaming'
        ? ' Streaming'
        : ' Download'
    }`;
  return (
    <DropdownWrapper label={label}>
      <SearchableMenu
        items={items}
        selectedItem={selectedItem || null}
        onChange={onChange}
        disabled={disabled}
        disabledMessage={disabledMessage}
        itemToString={itemToString}
        Button={getButtonWithDefaultLabel(clearFilter, defaultLabel)}
        Item={customItem || ListItem}
        isSubscriptionForm={isSubscriptionForm}
        ListLabel={ListLabel}
        showListLabel={!!ListLabel}
        onClickOutsideCustom={onClickOutsideCustom}
      >
        {children}
      </SearchableMenu>
    </DropdownWrapper>
  );
};

interface ITopicFilterCustomerDropdownProps {
  items: any[];
  selectedItem: any;
  onChange: (item: any) => void;
  disabled?: boolean;
  disabledMessage?: string;
  itemToString?: TItemToString;
  defaultLabel?: string;
  children?: any;
  isSubscriptionForm?: boolean;
  customLabel?: string;
  customItem?: any;
  clearFilter?: () => void;
  ListLabel?: () => ReactElement;
  onClickOutsideCustom?: () => void;
}

interface IListItemProps {
  item: any;
  isActive?: boolean;
  isHovered?: boolean;
}

export default TopicFilterCustomerDropdown;
